import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
	Button,
	Col,
	Form,
	Input,
	Modal,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
} from "antd";
import moment from "moment-jalaali";

import { CheckPermission, GetUrlParams, ObjectToURL } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals, Permissions, Types } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/NotificationForm";

const Component = ({ onSetRoute, parentName, permissions, routeName }) => {
	const initialState = { data: [], pagination: {}, categories: [] };
	const tableColumns = [
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: "60px",
			render: (value, record, index) =>
				(pagination.current - 1) * pagination.pageSize + index + 1,
		},
		{
			title: Resources.title,
			dataIndex: "title",
			className: "center-cell",
		},
		{
			title: Resources.generality,
			dataIndex: "general",
			className: "center-cell",
			width: "100px",
			render: (value) => (value ? Resources.general : Resources.private),
		},
		{
			title: `${Resources.type} ${Resources.info}`,
			dataIndex: "informType",
			width: "100px",
			className: "center-cell color-cell",
			render: (value) => {
				switch (value) {
					case Types.notifications.error:
						return <span className="red">{Resources.error}</span>;
					case Types.notifications.info:
						return <span className="blue">{Resources.info}</span>;
					case Types.notifications.warning:
						return <span className="orange">{Resources.warning}</span>;
					default:
						return <span className="green">{Resources.successfully}</span>;
				}
			},
		},
		{
			title: Resources.type,
			dataIndex: "type",
			className: "center-cell",
			width: "100px",
			render: (value) => {
				return value === "manual" ? Resources.manual : Resources.systematic;
			},
		},
		{
			title: Resources.status,
			className: "center-cell color-cell",
			dataIndex: "status",
			width: "80px",
			render: (value, record) =>
				permissions.status ? (
					<span
						className={`btn ${value ? "green" : "red"}`}
						onClick={() => handleStatus(record)}
					>
						{value ? Resources.active : Resources.inactive}
					</span>
				) : (
					<span className={value ? "green" : "red"}>
						{value ? Resources.active : Resources.inactive}
					</span>
				),
		},
		{
			title: `${Resources.date} ${Resources.record}`,
			dataIndex: "createdAt",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return (
					<>
						<span className="name">{date}</span>
						<small className="desc">{time}</small>
					</>
				);
			},
		},
		{
			title: `${Resources.date} ${Resources.expire}`,
			dataIndex: "expireAt",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return value === null ? (
					<>
						<span className="name">{Resources.unlimited}</span>
					</>
				) : (
					<>
						<span className="name">{date}</span>
						<small className="desc">{time}</small>
					</>
				);
			},
		},
		{
			title: Resources.action,
			dataIndex: "id",
			className: "center-cell",
			width: "120px",
			render: (value, record) => (
				<>
					{permissions.update && (
						<Tooltip title={Resources.edit}>
							<Button
								type="link"
								icon={<i className="fa fa-pencil-alt" />}
								onClick={() => handleForm(record)}
							/>
						</Tooltip>
					)}
					{permissions.delete && (
						<Tooltip title={Resources.delete}>
							<Button
								type="link"
								icon={<i className="fa fa-trash-alt" />}
								onClick={() => handleDelete(value)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const { data, loading, pagination, selected, categories } = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const fetchData = () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		API.notification
			.list({ ...params, pageSize: process.env.REACT_APP_PAGE_SIZE })
			.then(({ data, meta: { currentPage, perPage, total } }) => {
				setState((prevState) => ({
					...prevState,
					data,
					pagination: {
						current: currentPage,
						pageSize: perPage,
						total,
						showSizeChanger: false,
					},
					loading: false,
				}));
			})
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(
				`${pathname}${search}#${selected ? Modals.EDIT : Modals.CREATE}`
			);
		}
	};

	const handleDelete = (id) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "error",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.notification
					.delete(id)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleStatus = (selected) => {
		Modal.confirm({
			title: Resources.statusConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.notification
					.status(selected.id)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleTable = ({ current }) => {
		const params = GetUrlParams(search);
		const query = ObjectToURL({ ...params, page: current });

		navigate(`${pathname}?${query}`);
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate(`${pathname}?${search}`);
		});
	}, 1000);

	// const handleUsers = (param = "") => {
	// 	API.user
	// 		.list({ pageSize: process.env.REACT_APP_PAGE_SIZE, search: param })
	// 		.then(({ data }) => {
	// 			setState((prevState) => ({
	// 				...prevState,
	// 				users: data.map(({ fullName, phone, id }) => ({
	// 					label: fullName || phone,
	// 					value: id,
	// 				})),
	// 			}));
	// 		});
	// };

	const handleCategories = (param = "") => {
		API.category
			.list()
			.then(({ data }) => {
				setState((prevState) => ({
					...prevState,
					categories: data.map(({ title, id }) => ({
						label: title,
						value: id,
					})),
				}));
			});
	};

	const componentDidUpdate = () => {
		fetchData();
		handleCategories();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader
				title={Resources.notifications}
				extra={
					permissions.create && (
						<Button
							type="primary"
							onClick={() => handleForm()}
							loading={loading}
						>
							{Resources.add}
						</Button>
					)
				}
			/>

			<Form
				layout="vertical"
				form={form}
			>
				<Row gutter={24}>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="search">
							<Input
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.search}
								allowClear
								autoFocus
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="infromType">
							<Select
								placeholder={`${Resources.type} ${Resources.info}`}
								options={[
									{
										value: Types.notifications.success,
										label: Resources.successfully,
									},
									{ value: Types.notifications.info, label: Resources.info },
									{
										value: Types.notifications.warning,
										label: Resources.warning,
									},
									{ value: Types.notifications.error, label: Resources.error },
								]}
								onChange={handleFilter}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="type">
							<Select
								placeholder={Resources.type}
								options={[
									{ value: "manual", label: Resources.manual },
									{ value: "system", label: Resources.systematic },
								]}
								onChange={handleFilter}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="categories">
							<Select
								placeholder={Resources.category}
								options={categories}
								onChange={handleFilter}
								onSearch={(e) => handleCategories(e)}
								showSearch
								optionFilterProp="label"
								allowClear
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				onChange={handleTable}
				pagination={pagination}
				scroll={{ x: 930 }}
				rowKey="id"
			/>

			<FormModal
				data={selected}
				onUpdate={handleForm}
				options={categories}
			/>
		</>
	);
};

export default connect(
	(state) => {
		return {
			permissions: {
				create: CheckPermission(
					state.user.permissions,
					Permissions.NOTIFICATION_CREATE
				),
				delete: CheckPermission(
					state.user.permissions,
					Permissions.NOTIFICATION_DELETE
				),
				status: CheckPermission(
					state.user.permissions,
					Permissions.NOTIFICATION_STATUS
				),
				update: CheckPermission(
					state.user.permissions,
					Permissions.NOTIFICATION_UPDATE
				),
			},
		};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
